import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  CheckCircle,
  Star,
  Zap,
  Shield,
  CreditCard,
  Clock,
  BarChart3,
  Globe,
  DollarSign,
  FileText,
  Users,
  TrendingUp,
  Calendar,
  ChevronDown,
  ChevronUp,
  Menu,
  X
} from 'lucide-react'

const features = [
  {
    icon: <FileText className="w-6 h-6" />,
    title: "Professional Invoices",
    description: "Create stunning, branded invoices in seconds with our customizable templates and automatic PDF generation."
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: "Recurring Billing",
    description: "Set up automatic invoices for subscription clients. Never miss a billing cycle again."
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "Client Portal",
    description: "Give clients a secure self-service portal to view invoices, download PDFs, and make payments online."
  },
  {
    icon: <Globe className="w-6 h-6" />,
    title: "Multi-Currency",
    description: "Invoice clients worldwide in their local currency. We handle the exchange rates automatically."
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: "Expense Tracking",
    description: "Track business expenses, categorize them, and generate expense reports for tax season."
  },
  {
    icon: <BarChart3 className="w-6 h-6" />,
    title: "Analytics Dashboard",
    description: "Get real-time insights into your revenue, outstanding invoices, and business growth trends."
  },
  {
    icon: <DollarSign className="w-6 h-6" />,
    title: "Tax Compliance",
    description: "Automatic tax calculations, support for GST, VAT, and sales tax. Generate tax reports instantly."
  },
  {
    icon: <Calendar className="w-6 h-6" />,
    title: "Payment Reminders",
    description: "Schedule automatic payment reminders. Get notified when invoices are viewed and paid."
  }
]

const steps = [
  {
    step: "01",
    title: "Create Your Invoice",
    description: "Choose a template, add your branding, and fill in the line items. Our intuitive editor makes it fast."
  },
  {
    step: "02",
    title: "Send to Client",
    description: "Send via email or share a secure link. Clients can view and pay from any device, anywhere."
  },
  {
    step: "03",
    title: "Get Paid Faster",
    description: "Accept online payments, track outstanding invoices, and send automatic reminders. Watch your revenue grow."
  }
]

const testimonials = [
  {
    name: "Sarah Mitchell",
    role: "Freelance Designer",
    content: "nCinq Invoice saved me hours every week. The automated reminders alone helped me reduce overdue invoices by 80%.",
    avatar: "SM",
    rating: 5
  },
  {
    name: "Michael Chen",
    role: "Digital Agency Owner",
    content: "The client portal is a game-changer. Clients love being able to self-serve, and I love not chasing payments.",
    avatar: "MC",
    rating: 5
  },
  {
    name: "Emily Rodriguez",
    role: "Consultant",
    content: "Multi-currency support made invoicing my international clients so much easier. Best invoicing tool I've used.",
    avatar: "ER",
    rating: 5
  }
]

const pricing = [
  {
    name: "Starter",
    price: { monthly: 0, yearly: 0 },
    description: "Perfect for freelancers just getting started",
    features: [
      "Up to 10 invoices/month",
      "Basic invoice templates",
      "Email support",
      "PDF export",
      "1 client user"
    ],
    cta: "Get Started Free",
    highlight: false
  },
  {
    name: "Professional",
    price: { monthly: 29, yearly: 24 },
    description: "For growing businesses that need more power",
    features: [
      "Unlimited invoices",
      "Custom branding",
      "Recurring invoices",
      "Client portal",
      "Multi-currency",
      "Priority support",
      "5 client users"
    ],
    cta: "Start Free Trial",
    highlight: true
  },
  {
    name: "Enterprise",
    price: { monthly: 99, yearly: 79 },
    description: "Advanced features for large organizations",
    features: [
      "Everything in Professional",
      "API access",
      "Advanced analytics",
      "Team collaboration",
      "Custom integrations",
      "Dedicated account manager",
      "Unlimited client users"
    ],
    cta: "Contact Sales",
    highlight: false
  }
]

const faqs = [
  {
    question: "Can I try nCinq Invoice before committing to a paid plan?",
    answer: "Absolutely! We offer a 14-day free trial on all paid plans. No credit card required. Experience all features risk-free and see how nCinq can transform your billing process."
  },
  {
    question: "What payment methods can I accept through the client portal?",
    answer: "We support all major credit cards (Visa, MasterCard, American Express), bank transfers, and PayPal. Our secure payment processing ensures PCI compliance and protects your clients' sensitive information."
  },
  {
    question: "Is my data secure with nCinq Invoice?",
    answer: "Security is our top priority. We use bank-level encryption (256-bit SSL) for all data transmission. Your data is stored in secure, redundant cloud servers with daily backups. We're also GDPR compliant."
  },
  {
    question: "Can I switch plans at any time?",
    answer: "Yes, you can upgrade or downgrade your plan at any time. When upgrading, you'll get immediate access to new features. When downgrading, changes take effect at the start of your next billing cycle."
  },
  {
    question: "Do you offer discounts for non-profits or educational organizations?",
    answer: "Yes! We offer special pricing for qualified non-profit organizations and educational institutions. Contact our sales team with documentation to learn more about our discount programs."
  }
]

const companies = [
  { name: "TechCorp", initial: "TC" },
  { name: "StartupXYZ", initial: "SX" },
  { name: "DesignLab", initial: "DL" },
  { name: "CloudNine", initial: "CN" },
  { name: "InnovateCo", initial: "IC" }
]

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [openFaq, setOpenFaq] = useState(null)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
    setMobileMenuOpen(false)
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="nCinq Invoice" className="h-10 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features') }} className="text-gray-600 hover:text-primary-600 transition-colors">Features</a>
              <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works') }} className="text-gray-600 hover:text-primary-600 transition-colors">How it Works</a>
              <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing') }} className="text-gray-600 hover:text-primary-600 transition-colors">Pricing</a>
              <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq') }} className="text-gray-600 hover:text-primary-600 transition-colors">FAQ</a>
            </div>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login" className="text-gray-600 hover:text-primary-600 font-medium transition-colors">
                Sign In
              </Link>
              <Link to="/register" className="btn btn-primary">
                Start Free Trial
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-gray-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 animate-fade-in">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features') }} className="block py-2 text-gray-600 hover:text-primary-600">Features</a>
              <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works') }} className="block py-2 text-gray-600 hover:text-primary-600">How it Works</a>
              <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing') }} className="block py-2 text-gray-600 hover:text-primary-600">Pricing</a>
              <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq') }} className="block py-2 text-gray-600 hover:text-primary-600">FAQ</a>
              <hr className="border-gray-100" />
              <Link to="/login" className="block py-2 text-gray-600 font-medium">Sign In</Link>
              <Link to="/register" className="btn btn-primary w-full text-center">Start Free Trial</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-50 rounded-full text-primary-700 text-sm font-medium mb-8">
              <span style={{ fontFamily: "'Dancing Script', cursive", fontSize: '1.2rem' }}>by Christopher Ogleton</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Invoicing made simple.
              <span className="text-primary-600"> Business made scalable.</span>
            </h1>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Automate recurring billing, track expenses, and get paid faster with the all-in-one invoicing platform designed for freelancers, agencies, and growing businesses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/register" className="btn btn-primary text-lg px-8 py-4">
                Start Free Trial
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <Link to="/demo" className="btn btn-secondary text-lg px-8 py-4">
                View Demo
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-gray-400">
              <div className="flex items-center gap-2">
                <Shield size={18} />
                <span className="text-sm">Bank-level security</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard size={18} />
                <span className="text-sm">50+ payment methods</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={18} />
                <span className="text-sm">99.9% uptime</span>
              </div>
            </div>
          </div>

          {/* Dashboard Preview */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10 pointer-events-none" />
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden mx-4 lg:mx-0">
              <div className="bg-gray-100 px-4 py-3 flex items-center gap-2 border-b border-gray-200">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="p-6 bg-gray-50">
                <div className="grid grid-cols-12 gap-4">
                  {/* Sidebar */}
                  <div className="col-span-2 hidden sm:block">
                    <div className="space-y-3">
                      <div className="h-8 bg-primary-100 rounded-lg" />
                      <div className="h-6 bg-gray-200 rounded-lg w-3/4" />
                      <div className="h-6 bg-gray-200 rounded-lg w-1/2" />
                      <div className="h-6 bg-gray-200 rounded-lg w-2/3" />
                    </div>
                  </div>
                  {/* Main Content */}
                  <div className="col-span-12 sm:col-span-10">
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                      <div className="flex justify-between items-center mb-6">
                        <div>
                          <div className="h-7 bg-gray-900 rounded-lg w-32 mb-2" />
                          <div className="h-4 bg-gray-200 rounded-lg w-48" />
                        </div>
                        <div className="flex gap-2">
                          <div className="h-10 bg-primary-600 rounded-lg w-24" />
                          <div className="h-10 bg-gray-200 rounded-lg w-24" />
                        </div>
                      </div>
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gray-100 rounded-full" />
                              <div>
                                <div className="h-4 bg-gray-200 rounded-lg w-32 mb-1" />
                                <div className="h-3 bg-gray-100 rounded-lg w-20" />
                              </div>
                            </div>
                            <div className="h-5 bg-gray-200 rounded-lg w-16" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything you need to run your business
            </h2>
            <p className="text-xl text-gray-600">
              Powerful features designed to save you time, reduce admin work, and help you get paid faster.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-6 bg-gray-50 rounded-2xl hover:bg-white hover:shadow-lg hover:shadow-gray-200/50 transition-all duration-300 border border-transparent hover:border-gray-100"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600 mb-4 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How nCinq works
            </h2>
            <p className="text-xl text-gray-600">
              Get started in minutes and streamline your entire invoicing workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 z-10">
                  {step.step}
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-0.5 bg-gray-200">
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-primary-600 rounded-full" />
                  </div>
                )}
                <div className="pt-16 pb-4 px-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Loved by thousands of businesses
            </h2>
            <p className="text-xl text-gray-600">
              See what our customers have to say about their experience with nCinq Invoice.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="p-6 bg-gray-50 rounded-2xl">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Choose the plan that's right for your business. No hidden fees.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4">
              <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className="relative w-14 h-7 bg-primary-600 rounded-full transition-colors"
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'}`} />
              </button>
              <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>
                Yearly
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  Save 20%
                </span>
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricing.map((plan, index) => (
              <div
                key={index}
                className={`relative p-8 bg-white rounded-2xl transition-all duration-300 ${plan.highlight ? 'shadow-xl ring-2 ring-primary-600 scale-105' : 'shadow-lg hover:shadow-xl'}`}
              >
                {plan.highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary-600 text-white text-sm font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-500 text-sm mb-6">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">${billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly}</span>
                  <span className="text-gray-500">/month</span>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-gray-500 mt-1">Billed annually</p>
                  )}
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-3 px-4 rounded-xl font-semibold transition-colors ${plan.highlight ? 'btn btn-primary' : 'btn btn-secondary'}`}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently asked questions
            </h2>
            <p className="text-xl text-gray-600">
              Have questions? We've got answers. If you don't see your question here, feel free to contact us.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp size={20} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-400" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 text-gray-600 leading-relaxed animate-fade-in">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to streamline your invoicing?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join thousands of businesses that trust nCinq Invoice to manage their billing. Start your free trial today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="btn bg-white text-primary-600 hover:bg-gray-100 text-lg px-8 py-4">
              Start Free Trial
              <ArrowRight size={20} className="ml-2" />
            </Link>
            <Link to="/contact" className="btn border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-4">
              Contact Sales
            </Link>
          </div>
          <p className="text-primary-200 text-sm mt-6">
            No credit card required. 14-day free trial. Cancel anytime.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2 lg:col-span-1">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img src="/logo-white.png" alt="nCinq Invoice" className="h-10 w-auto" />
              </Link>
              <p className="text-sm mb-4">
                The modern invoicing platform for freelancers, agencies, and growing businesses.
              </p>
              <div className="flex gap-4">
                <a href="#" className="hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                  </svg>
                </a>
                <a href="#" className="hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                </a>
                <a href="#" className="hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Changelog</a></li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Press</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            {/* Resources Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Guides</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Templates</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Community</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm">
              © 2024 nCinq Invoice. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Globe size={14} />
                English
              </span>
              <span className="flex items-center gap-1">
                <DollarSign size={14} />
                USD
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
